import { Injectable } from '@angular/core';

import { InfillClippedByFourPointsService } from './kinds/infills-clipped-by-four-points.service';
import { Infills } from './model/infills';
import { StageService } from '../../stage.service';
import { SizeHelperService } from '../../size-helper.service';
import { DataForDisplayingMenuItemsService } from '../../data-for-displaying-menu-items.service';
import { HelperLinesService } from '../../helper-lines.service';
import { DistanceHelperService } from '../../distance-helper.service';
import { InfillsKindsUtilsService } from './kinds/tools/infills-kinds-utils.service';
import { InfillTypes, isPanelInfill } from './model/infill-types';

declare const THREE: any;

@Injectable()
export class InfillsForTiersService {
  currentParams: any;
  intermediatePost: any;

  glass: any;
  glassClear: any;
  glassSmoke: any;
  glassDark: any;

  laserCutAgama: any;
  laserCutBound: any;
  laserCutCubist: any;
  laserCutPisces: any;
  laserCutRhythm: any;
  laserCutSeagrass: any;
  laserCutSverra: any;
  laserCutTrillium: any;
  laserCutPulse: any;

  steelMeshAllure: any;
  steelMeshDazzle: any;
  steelMeshMetropolitan: any;
  steelMeshHorizon: any;

  verticalPicket: any;
  verticalCable: any;
  singleHorizontalCable: any;
  stairGlass: any;
  horizontalCable: any;
  handrail: any;
  baserail: any;

  constructor(
    private stage: StageService,
    private sizeHelper: SizeHelperService,
    private dataForMenu: DataForDisplayingMenuItemsService,
    private helperLinesService: HelperLinesService,
    private distanceHelper: DistanceHelperService,
    private infillsUtilsService: InfillsKindsUtilsService,
    private infillClippedByFourPointsService: InfillClippedByFourPointsService
  ) {}

  addInfill(
    postPos1,
    postPos2,
    pos1,
    pos2,
    infillHeight,
    infillWidth,
    infillCenterPosition,
    angleY,
    angleZ,
    infills,
    railingComponent
  ) {
    this.updateCurrentParams();
    this.intermediatePost = railingComponent.intermediatePost;

    this.glass = railingComponent.glass;
    // glass / laser-cut / steel-mesh panel models are keyed by their InfillTypes name
    Object.keys(InfillTypes).forEach(key => (this[key] = railingComponent[key]));

    this.verticalPicket = railingComponent.verticalPicket;
    this.verticalCable = railingComponent.verticalCable;
    this.singleHorizontalCable = railingComponent.singleHorizontalCable;
    this.horizontalCable = railingComponent.horizontalCable;
    this.stairGlass = railingComponent.stairGlass;
    this.handrail = railingComponent.handrail;
    this.baserail = railingComponent.baserail;

    switch (this.currentParams.infill) {
      case 'verticalCable':
        {
          this.setPositionsForVerticalCables(pos1, pos2, infillHeight, infills);
        }
        break;
      case 'horizontalCableBaserail':
        {
          this.addHorizontalCables(
            pos1,
            pos2,
            infillHeight,
            infillWidth,
            infillCenterPosition,
            angleY,
            angleZ,
            true,
            infills
          );
        }
        break;
      case 'horizontalCable':
        {
          this.addHorizontalCables(
            pos1,
            pos2,
            infillHeight,
            infillWidth,
            infillCenterPosition,
            angleY,
            angleZ,
            false,
            infills
          );
        }
        break;
      default:
        // glass / laser-cut / steel-mesh panel infills (see InfillTypes)
        if (isPanelInfill(this.currentParams.infill)) {
          this.addAndCutInfillForTiers(
            postPos1,
            postPos2,
            infills,
            angleY,
            this.currentParams.infill
          );
        }
        break;
      case 'verticalPicket':
        {
          this.setPositionsForVerticalPicket(pos1, pos2, infillHeight, infills);
        }
        break;
    }
  }

  setPositionsForVerticalCables(pos1, pos2, infillHeight, infills) {
    const length = pos1.distanceTo(pos2) - 10;
    let count, offset;

    for (let i = 1; length / i > 7.62; i++) {
      if (length / i < 10.16) {
        count = i - 1;
        offset = length / i;
      }
    }

    const position = pos1.clone();
    const direction = new THREE.Vector3(pos2.x - pos1.x, pos2.y - pos1.y, pos2.z - pos1.z);
    direction.setLength(5);
    position.add(direction);
    direction.setLength(offset);

    for (let i = 0; i < count; i++) {
      position.add(direction);
      let pos = position.clone();
      this.addVerticalCable(pos, infillHeight, infills);
    }
  }

  addVerticalCable(position, infillHeight, infills) {
    const obj = this.verticalCable.clone();
    obj.children.forEach(ch => (ch.castShadow = true));
    obj.visible = true;
    const scaleY = infillHeight / this.verticalCable.userData.entity.height;
    obj.scale.y = scaleY;

    const height = infillHeight / 2 + position.y;

    obj.position.set(position.x, height, position.z);

    this.stage.add(obj);
    infills.push(obj);
  }

  addHorizontalCables(
    pos1,
    pos2,
    infillHeight,
    infillWidth,
    infillCenterPosition,
    angleY,
    angleZ,
    baserail,
    infills
  ) {
    const position1 = new THREE.Vector3(
      infillCenterPosition.x,
      (pos2.y + pos1.y) / 2,
      infillCenterPosition.z
    );
    const position2 = new THREE.Vector3(
      infillCenterPosition.x,
      (pos2.y + pos1.y) / 2 + infillHeight,
      infillCenterPosition.z
    );

    const length = position1.distanceTo(position2) - 2;
    let count, gap;

    // using a singleHorizontalCable
    // which is different than a horizontalCable
    // than includes all cables in one model
    for (let i = 1; length / i > 8.62; i++) {
      if (length / i < 10.16) {
        count = i - 1;
        gap = length / i;
      }
    }

    const position = position2.clone();
    const direction = new THREE.Vector3(
      position1.x - position2.x,
      position1.y - position2.y,
      position1.z - position2.z
    );
    direction.setLength(1);
    position.add(direction);
    direction.setLength(gap);

    const scaleX = (pos1.distanceTo(pos2) + 5) / 150;

    for (let i = 0; i < count; i++) {
      this.addHorizontalCable(position, scaleX, angleY, angleZ, direction, infills);
    }

    if (!baserail) {
      this.addHorizontalCable(position, scaleX, angleY, angleZ, direction, infills);
    }
  }

  addHorizontalCable(position, scaleX, angleY, angleZ, direction, infills) {
    position.add(direction);
    const obj = this.singleHorizontalCable.clone();
    obj.children.forEach(ch => (ch.castShadow = true));
    obj.visible = true;
    obj.scale.x = scaleX;

    obj.rotation.z -= angleY;
    obj.rotation.y += angleZ - Math.PI / 2;

    obj.position.set(position.x, position.y, position.z);

    this.stage.add(obj);
    infills.push(obj);
  }

  addAndCutInfillForTiers(pos1, pos2, infills, angleY, infillType: string) {
    if (!this[infillType]) {
      console.warn(`Infill type '${infillType}' not found`);
      return;
    }
    this.infillClippedByFourPointsService.add(
      pos1,
      pos2,
      this[infillType],
      this.baserail,
      this.handrail,
      this.intermediatePost,
      infills,
      Infills.tires,
      angleY
    );
  }

  setPositionsForVerticalPicket(pos1, pos2, infillHeight, infills) {
    const length = pos1.distanceTo(pos2) - 10;

    for (var i = 1; length / i > 7.62; i++) {
      if (length / i < 10.16) {
        var count = i - 1;
        var offset = length / i;
      }
    }

    const position = pos1.clone();
    const direction = new THREE.Vector3(pos2.x - pos1.x, pos2.y - pos1.y, pos2.z - pos1.z);
    direction.setLength(5);
    position.add(direction);
    direction.setLength(offset);

    for (var i = 0; i < count; i++) {
      position.add(direction);
      const pos = position.clone();
      this.addVerticalPicket(pos, infillHeight, infills);
    }
  }

  addVerticalPicket(position, infillHeight, infills) {
    const obj = this.verticalPicket.clone();
    obj.children.forEach(ch => (ch.castShadow = true));
    obj.visible = true;
    const scaleY = infillHeight / this.verticalPicket.userData.entity.height;
    obj.scale.y = scaleY;

    const height = infillHeight / 2 + position.y;

    obj.position.set(position.x, height, position.z);

    this.stage.add(obj);
    infills.push(obj);
  }

  updateCurrentParams() {
    this.currentParams = this.dataForMenu.getCurrentParams();
  }
}



// WEBPACK FOOTER //
// ./src/app/services/railing/infills/infills-for-tiers.service.ts